export const MAX_CONSUME_ATTEMPTS = 3;
export const CONSUME_BACKOFF_BASE_MS = 250;

export function consumeBackoffMs(attempt: number): number {
  return Math.floor(CONSUME_BACKOFF_BASE_MS * 2 ** (attempt - 1));
}

export class ConsumeCancelledError extends Error {
  constructor() {
    super('The consume operation was cancelled.');
    this.name = 'ConsumeCancelledError';
  }
}

export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new ConsumeCancelledError();
  }
}

export function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new ConsumeCancelledError());
      return;
    }
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new ConsumeCancelledError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export type ConsumeActiveProducerDeps = {
  waitForMediasoupReady(): Promise<void>;
  createAttemptSignal(): AbortSignal;
  getActiveProducerId(): string | null;
  getConsumedProducerId(): string | null;
  consumeOnce(producerId: string, signal: AbortSignal): Promise<void>;
  onConsumed(producerId: string): void;
  onAttemptFailed(): void;
  sendClientEvent(eventName: string): Promise<void>;
  delay(ms: number, signal: AbortSignal): Promise<void>;
  log(message: string): void;
};

function isCancellation(error: unknown, signal: AbortSignal): boolean {
  return error instanceof ConsumeCancelledError || signal.aborted;
}

export async function consumeActiveProducer(deps: ConsumeActiveProducerDeps): Promise<void> {
  await deps.sendClientEvent('[ConsumeActiveProducer] START');
  deps.log('[ConsumeActiveProducer] START');
  await deps.waitForMediasoupReady();
  deps.log('[ConsumeActiveProducer] mediasoupReeady.Task done');

  const signal = deps.createAttemptSignal();

  const producerId = deps.getActiveProducerId();
  if (producerId == null || producerId === deps.getConsumedProducerId()) {
    deps.log(`[ConsumeActiveProducer] Returning (null or already consumed) - producerId: ${producerId ?? ''}`);
    return;
  }

  deps.log(`[Consume] Start: ${producerId}`);

  try {
    for (let attempt = 1; attempt <= MAX_CONSUME_ATTEMPTS; attempt++) {
      throwIfAborted(signal);

      try {
        await deps.sendClientEvent(
          `[Consume] Attempting to consume producer ${producerId}, attempt ${attempt}/${MAX_CONSUME_ATTEMPTS}`,
        );
        deps.log(`[Consume] consume attempt ${attempt}/${MAX_CONSUME_ATTEMPTS}`);

        await deps.consumeOnce(producerId, signal);

        deps.onConsumed(producerId);
        deps.log(`[Consume] Success: ${producerId}`);
        return;
      } catch (error) {
        if (isCancellation(error, signal)) {
          throw error instanceof ConsumeCancelledError ? error : new ConsumeCancelledError();
        }

        const message = error instanceof Error ? error.message : String(error);
        await deps.sendClientEvent(`[Consume] Attempt ${attempt} failed: ${message}`);
        deps.log(`[Consume] Attempt ${attempt}/${MAX_CONSUME_ATTEMPTS} failed: ${message}`);
        deps.onAttemptFailed();

        if (attempt === MAX_CONSUME_ATTEMPTS) {
          break;
        }

        await deps.delay(consumeBackoffMs(attempt), signal);
      }
    }
  } catch (error) {
    if (!(error instanceof ConsumeCancelledError)) {
      throw error;
    }
    deps.log(`[Consume] Cancelled for producer ${producerId}`);
  } finally {
    deps.log('[ConsumeActiveProducer] END');
    await deps.sendClientEvent('[ConsumeActiveProducer] END');
  }
}
